import { useRef, useState, FormEvent } from 'react'
import { motion } from 'framer-motion'
import PageLayout from '../components/PageLayout'
import PageHero from '../components/PageHero'
import { useInView } from '../hooks/useInView'

const channels = [
  {
    label: 'Sales & demos',
    email: 'hello@oakplatforms.com',
    body: 'Exploring Oak for your community? We will walk you through a live demo tuned to your use case.',
  },
  {
    label: 'Support',
    email: 'support@oakplatforms.com',
    body: 'Already running an Oak marketplace and need a hand? Operators get same-day responses on weekdays.',
  },
  {
    label: 'Partnerships',
    email: 'partners@oakplatforms.com',
    body: 'Integrations, platform partnerships, and agencies building on Oak for their clients.',
  },
  {
    label: 'Press',
    email: 'press@oakplatforms.com',
    body: 'Journalists and analysts — we respond within one business day. Full press kit on the Press page.',
  },
  {
    label: 'Security',
    email: 'security@oakplatforms.com',
    body: 'Report vulnerabilities responsibly. See our disclosure policy on the GDPR and Privacy pages.',
  },
  {
    label: 'Careers',
    email: 'careers@oakplatforms.com',
    body: 'Open roles live on the Careers page. Do not see your role? Write anyway.',
  },
]

function ContactForm() {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { threshold: 0.15 })
  const [submitted, setSubmitted] = useState(false)
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    topic: 'Sales & demos',
    message: '',
  })

  const onChange =
    (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [field]: e.target.value })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setSubmitted(true)
  }

  return (
    <section ref={ref} className="rounded-section bg-white px-6 py-20 md:px-16 md:py-28">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7 }}
        className="mx-auto grid max-w-content grid-cols-1 gap-12 md:grid-cols-12 md:gap-16"
      >
        <div className="md:col-span-5">
          <span className="text-sm font-semibold uppercase tracking-widest text-gold-500">
            Send us a note
          </span>
          <h2 className="mt-4 font-serif text-[14vw] leading-[1] tracking-[-1.5px] md:text-[clamp(32px,5vw,56px)] text-oak-black">
            One form. We route it to the right person.
          </h2>
          <p className="mt-6 text-[clamp(16px,1.5vw,19px)] leading-[1.5] tracking-[-0.2px] text-oak-black/60">
            Tell us what you are working on and how we can help. We reply within one business day.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-3xl border border-oak-black/5 bg-oak-cream p-8 md:col-span-7 md:p-10"
        >
          {submitted ? (
            <div className="flex min-h-[340px] flex-col items-center justify-center text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-oak-green text-white">
                ✓
              </div>
              <h3 className="mt-6 font-serif text-[clamp(24px,3vw,32px)] leading-tight tracking-[-0.8px] text-oak-black">
                Thanks — message received.
              </h3>
              <p className="mt-3 max-w-[360px] text-[15px] leading-[1.5] tracking-[-0.2px] text-oak-black/60">
                Someone from the team will reply to {form.email || 'you'} within one business day.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div className="md:col-span-1">
                <label className="text-[13px] font-semibold uppercase tracking-[0.08em] text-oak-black/60">
                  Name
                </label>
                <input
                  required
                  value={form.name}
                  onChange={onChange('name')}
                  className="mt-2 w-full rounded-xl border border-oak-black/10 bg-white px-4 py-3 text-[15px] tracking-[-0.2px] text-oak-black placeholder:text-oak-black/30 focus:border-oak-black/40 focus:outline-none"
                  placeholder="Alex Rivera"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-[13px] font-semibold uppercase tracking-[0.08em] text-oak-black/60">
                  Email
                </label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={onChange('email')}
                  className="mt-2 w-full rounded-xl border border-oak-black/10 bg-white px-4 py-3 text-[15px] tracking-[-0.2px] text-oak-black placeholder:text-oak-black/30 focus:border-oak-black/40 focus:outline-none"
                  placeholder="you@company.com"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-[13px] font-semibold uppercase tracking-[0.08em] text-oak-black/60">
                  Community or company
                </label>
                <input
                  value={form.company}
                  onChange={onChange('company')}
                  className="mt-2 w-full rounded-xl border border-oak-black/10 bg-white px-4 py-3 text-[15px] tracking-[-0.2px] text-oak-black placeholder:text-oak-black/30 focus:border-oak-black/40 focus:outline-none"
                  placeholder="Optional"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-[13px] font-semibold uppercase tracking-[0.08em] text-oak-black/60">
                  Topic
                </label>
                <select
                  value={form.topic}
                  onChange={onChange('topic')}
                  className="mt-2 w-full rounded-xl border border-oak-black/10 bg-white px-4 py-3 text-[15px] tracking-[-0.2px] text-oak-black focus:border-oak-black/40 focus:outline-none"
                >
                  {channels.map((c) => (
                    <option key={c.label}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-[13px] font-semibold uppercase tracking-[0.08em] text-oak-black/60">
                  Message
                </label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={onChange('message')}
                  className="mt-2 w-full resize-none rounded-xl border border-oak-black/10 bg-white px-4 py-3 text-[15px] leading-[1.5] tracking-[-0.2px] text-oak-black placeholder:text-oak-black/30 focus:border-oak-black/40 focus:outline-none"
                  placeholder="Tell us about your community and what you are trying to do."
                />
              </div>
              <div className="md:col-span-2">
                <button
                  type="submit"
                  className="inline-flex items-center gap-3 rounded-pill bg-oak-black px-6 py-3 text-[15px] font-medium tracking-[-0.3px] text-white transition-opacity hover:opacity-80"
                >
                  Send message
                  <span className="h-6 w-6 rounded-full bg-oak-green" />
                </button>
              </div>
            </div>
          )}
        </form>
      </motion.div>
    </section>
  )
}

export default function ContactPage() {
  return (
    <PageLayout>
      <PageHero
        eyebrow="Contact"
        title="Talk to a human."
        description="Whether you are launching a marketplace, covering one, or thinking about building on top of Oak — write to us."
      />
      <ContactForm />
    </PageLayout>
  )
}
